import React from "react";
import {
    Heart,
    Moon,
    Waves,
    ShieldPlus,
    Activity,
    Home as HomeIcon,
    HeartPulse,
    Sparkles,
    Wind,
    Compass,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 PETS brochure for The Reset Sanctuary.
 * Page 1: Hero · Problem · Solution · From→To · 4 Benefits
 * Page 2: Five Outcomes · Equipment Toolkit · Tatva K · Programmes · CTA & Contact
 * Aesthetic mirrors /brochure-realestate.
 * Compliance: no banned terms outside the Berklee credential.
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-pets-page"
        data-testid={testid}
        style={{
            width: 794,
            height: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        <div style={{ position: "absolute", inset: 18, border: "1px solid rgba(201,162,39,0.22)", pointerEvents: "none" }} />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1, marginBottom: 14 }}>
        <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ height: 58 }} />
        <div style={{ textAlign: "right" }}>
            <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: 0, fontWeight: 500 }}>{kicker}</p>
            <p style={{ color: "#888", fontSize: 8.5, letterSpacing: "0.2em", textTransform: "uppercase", margin: "4px 0 0" }}>Dubai · RAK · In-Home UAE</p>
        </div>
    </header>
);

const PageFooter = ({ pageNum, tagline }) => (
    <div style={{ marginTop: "auto", paddingTop: 14, borderTop: `1px solid ${GOLD_LINE_SOFT}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1, fontSize: 8, color: "#666", letterSpacing: "0.18em", textTransform: "uppercase", gap: 12 }}>
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD, textAlign: "center", flex: 1 }}>{tagline}</span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 10 }}>
        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500 }}>{kicker}</p>
        <h2 className="font-serif" style={{ color, fontSize: 20, fontWeight: 500, margin: 0, lineHeight: 1.1, letterSpacing: "-0.005em" }}>{title}</h2>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div data-testid={testid} style={{ padding: "14px 13px", border: `1px solid ${GOLD_LINE}`, display: "flex", flexDirection: "column", gap: 6, backgroundColor: "rgba(201,162,39,0.025)" }}>
        <Icon size={24} color={GOLD} strokeWidth={1.4} />
        <p style={{ color: "#fff", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", margin: 0, fontWeight: 500, lineHeight: 1.25 }}>{label}</p>
        <p style={{ color: "#aaa", fontSize: 8.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>{body}</p>
    </div>
);

const SmallBenefit = ({ Icon, label, body, testid }) => (
    <div data-testid={testid} style={{ padding: "12px 10px", border: `1px solid ${GOLD_LINE_SOFT}`, display: "flex", flexDirection: "column", gap: 6, alignItems: "center", textAlign: "center" }}>
        <Icon size={22} color={GOLD} strokeWidth={1.4} />
        <p style={{ color: "#fff", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", margin: 0, fontWeight: 500, lineHeight: 1.25 }}>{label}</p>
        <p style={{ color: "#aaa", fontSize: 8.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>{body}</p>
    </div>
);

const Pillar = ({ title, body }) => (
    <div style={{ padding: "12px 12px", borderTop: `1px solid ${GOLD_LINE}` }}>
        <p style={{ color: GOLD, fontSize: 8.5, letterSpacing: "0.22em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500, lineHeight: 1.3 }}>{title}</p>
        <p style={{ color: "#bbb", fontSize: 9, lineHeight: 1.55, margin: 0, fontWeight: 300 }}>{body}</p>
    </div>
);

const Programme = ({ tier, headline, subtitle, items, featured, testid }) => (
    <div
        style={{
            padding: "16px 14px 18px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 7,
            height: "100%",
        }}
        data-testid={testid}
    >
        {featured && (
            <span style={{ position: "absolute", top: -10, left: 14, backgroundColor: GOLD, color: "#0a0a0a", fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase", padding: "3px 10px", fontWeight: 600 }}>
                Most Chosen
            </span>
        )}
        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, fontWeight: 500 }}>{tier}</p>
        <p className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 400, margin: 0, lineHeight: 1.15 }}>{subtitle}</p>
        <p style={{ color: "#bbb", fontSize: 9.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>{headline}</p>
        <div style={{ borderTop: `1px solid ${GOLD_LINE_SOFT}`, marginTop: 2, paddingTop: 8 }}>
            {items.map((it, i) => (
                <p key={i} style={{ color: "#fff", fontSize: 9.5, margin: "3px 0", lineHeight: 1.4 }}>· {it}</p>
            ))}
        </div>
    </div>
);

const BrochurePets = () => {
    const petsWa = waUrl("Hi, I have a pet at home and I'd like to book an in-home Sound Recovery session.");

    return (
        <main data-testid="brochure-pets-page" style={{ backgroundColor: "#0a0a0a", minHeight: "100vh", padding: "40px 16px 80px", color: "#bbb" }}>
            <div className="brochure-screen-only" style={{ maxWidth: 794, margin: "0 auto 24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <p style={{ color: "#888", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", margin: 0 }}>Pet Recovery — A4 · 2 pages</p>
                <button onClick={() => window.print()} className="btn-gold-solid" data-testid="brochure-pets-print-btn" style={{ cursor: "pointer", fontFamily: "Inter, sans-serif" }}>Save as PDF / Print</button>
            </div>

            <style>{`@media print { @page { size: A4; margin: 0; } body, main { background: #fff !important; padding: 0 !important; } header[data-testid="main-navigation"], a[data-testid="floating-whatsapp-btn"], footer, .brochure-screen-only { display: none !important; } .brochure-page { box-shadow: none !important; margin: 0 !important; page-break-after: always; } .brochure-page:last-child { page-break-after: auto; } }`}</style>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-pets-a4">
                <PageHeader kicker="Pet Recovery Sessions · In-Home" />

                {/* HERO HEADLINE */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }}>
                    <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: "0 0 8px", fontWeight: 500 }}>
                        Precision Sound Recovery for the Animals You Love
                    </p>
                    <h1 className="font-serif" style={{ color: "#fff", fontSize: 32, fontWeight: 400, lineHeight: 1.04, letterSpacing: "-0.025em", margin: "0 0 8px", textTransform: "uppercase" }}>
                        Anxious Pets. Aging Companions. <span style={{ color: GOLD }}>Real Recovery.</span>
                    </h1>
                </section>

                {/* HERO IMAGE */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 14, width: "100%", aspectRatio: "16 / 9", overflow: "hidden", border: `1px solid ${GOLD_LINE}` }} data-testid="pets-hero-image">
                    <img src="/brochure-assets/animals/dog-1.jpg" alt="Calm dog at home" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%", display: "block" }} />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.7) 100%)", pointerEvents: "none" }} />
                </section>

                {/* PROBLEM + SOLUTION */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div data-testid="pets-problem">
                        <SectionTitle kicker="The Problem" title="The behaviours nothing else fixes." />
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.6, margin: 0, fontWeight: 300 }}>
                            Separation anxiety. Thunderstorm and fireworks panic. Rescue trauma. Vet-day shutdown. Senior pets restless at night. Multi-pet households where the tension never resolves. Conventional training takes months. Drugs sedate but don&apos;t heal.
                        </p>
                    </div>
                    <div data-testid="pets-solution">
                        <SectionTitle kicker="The Solution" title="Precision Frequency Recovery." color={GOLD} />
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.6, margin: 0, fontWeight: 300 }}>
                            60 minutes. In your home. No drugs, no restraints. Engineered low-frequency sound and PEMF act directly on the brainstem and vagus nerve — your pet exits fight-or-flight without conscious participation. They simply receive.
                        </p>
                    </div>
                </section>

                {/* FROM → TO STRIP */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 14, padding: "12px 18px", border: `1px solid ${GOLD_LINE}`, display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 18, alignItems: "center" }} data-testid="pets-shift-strip">
                    <div style={{ textAlign: "right" }}>
                        <p style={{ color: "#777", fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px" }}>From</p>
                        <p style={{ color: "#bbb", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0, lineHeight: 1.6, textDecoration: "line-through", textDecorationColor: "rgba(201,162,39,0.5)" }}>
                            Anxiety · Pacing · Trauma · Restless Nights
                        </p>
                    </div>
                    <span style={{ color: GOLD, fontSize: 16, fontWeight: 300 }}>→</span>
                    <div>
                        <p style={{ color: GOLD, fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px" }}>To</p>
                        <p style={{ color: "#fff", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
                            Calm · Sleep · Trust · Soft Settle
                        </p>
                    </div>
                </section>

                {/* 4 BENEFITS */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }} data-testid="pets-benefits">
                    <SectionTitle kicker="The Benefits" title="What changes after one session." />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                        <BenefitTile Icon={Waves} label="Drops the Stress Response" body="Sub-audible frequencies signal the nervous system to exit fight-or-flight — without drugs or restraint." testid="pets-benefit-stress" />
                        <BenefitTile Icon={Moon} label="Deeper Sleep" body="Pets settle into actual REM sleep — not just lying down. Most owners see the change within one session." testid="pets-benefit-sleep" />
                        <BenefitTile Icon={HomeIcon} label="In-Home Delivery" body="We come to you. Your pet stays in their safe space. No transport stress. No clinic anxiety." testid="pets-benefit-home" />
                        <BenefitTile Icon={ShieldPlus} label="Non-Invasive" body="No needles, no drugs, no muzzles, no restraints. The animal chooses to settle in their own time." testid="pets-benefit-noninvasive" />
                    </div>
                </section>

                <PageFooter pageNum={1} tagline="Calm Pet · Calm Home" />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-pets-a4-page2">
                <PageHeader kicker="Science · Programmes · Contact" />

                {/* FIVE OUTCOMES */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 14 }} data-testid="pets-outcomes">
                    <SectionTitle kicker="What Owners Notice" title="Five outcomes in the home." />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                        <SmallBenefit Icon={HeartPulse} label="Lower Stress" body="Visible softening — breathing slows, body lowers, eyes soften." testid="pets-out-stress" />
                        <SmallBenefit Icon={Moon} label="Real Sleep" body="From restless dozing to deep, regenerative REM rest." testid="pets-out-sleep" />
                        <SmallBenefit Icon={Heart} label="Trust Returns" body="Rescue and trauma pets begin to lean in, not pull away." testid="pets-out-trust" />
                        <SmallBenefit Icon={Activity} label="Owner Bond" body="When the human regulates too, the animal regulates faster." testid="pets-out-bond" />
                        <SmallBenefit Icon={Compass} label="Household Calm" body="Multi-pet tension drops. The whole home settles into one rhythm." testid="pets-out-home" />
                    </div>
                </section>

                {/* EQUIPMENT */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }} data-testid="pets-equipment">
                    <SectionTitle kicker="Equipment & Frequencies" title="The in-home protocol toolkit." />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
                        {[
                            { src: "/brochure-assets/pemf-mat.jpg", label: "PEMF Recovery Mat", caption: "Pulsed electromagnetic fields used by NASA, Olympic athletes and leading veterinary practices. Reduces inflammation, supports joint comfort in aging pets, and dramatically deepens rest.", testid: "pets-equipment-pemf" },
                            { src: "/brochure-assets/tuning-forks.jpg", label: "Tuning Forks", caption: "Calibrated to 432 · 528 · 174 · 741 Hz. Applied near the pet's body to bypass the cognitive mind and act directly on the vagus nerve.", testid: "pets-equipment-forks" },
                        ].map((item) => (
                            <div key={item.testid} data-testid={item.testid} style={{ border: `1px solid ${GOLD_LINE}`, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                                <div style={{ width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
                                    <img src={item.src} alt={item.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                </div>
                                <div style={{ padding: "8px 12px 10px" }}>
                                    <p style={{ color: GOLD, fontSize: 8.5, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 3px", fontWeight: 500 }}>{item.label}</p>
                                    <p style={{ color: "#bbb", fontSize: 9, lineHeight: 1.45, margin: 0, fontWeight: 300 }}>{item.caption}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "2px 16px" }}>
                        <Pillar title="Setup · 10 min" body="Brief intake with the owner. Triggers, history, hopes. Equipment placed calmly in your pet&apos;s chosen room." />
                        <Pillar title="Session · 40 min · Debrief · 10 min" body="The pet wanders, lies down, or curls up — they decide. We hold the field. You receive a 48-hour observation guide." />
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12, padding: "12px 14px", border: `1px solid ${GOLD_LINE}`, display: "grid", gridTemplateColumns: "62px 1fr", gap: 12, alignItems: "center" }} data-testid="pets-practitioner">
                    <img src="/brochure-assets/tatva.png" alt="Tatva K" style={{ width: 62, height: 62, objectFit: "cover", objectPosition: "center 25%", borderRadius: "50%", border: `1px solid rgba(201,162,39,0.4)`, display: "block" }} />
                    <div>
                        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.32em", textTransform: "uppercase", margin: "0 0 4px", fontWeight: 500 }}>Led by Tatva K · Founder, The Reset Sanctuary</p>
                        <p style={{ color: "#fff", fontSize: 9, lineHeight: 1.5, margin: 0, fontWeight: 300 }}>
                            TEDx Speaker · 150M+ Aggregate Streams · Featured by BBC &amp; Rolling Stone · Berklee College of Music — Music Therapy Masterclass · ISTA Certified · IPHM Certified · Reiki Grandmaster · Featured Practitioner on Insight Timer
                        </p>
                    </div>
                </section>

                {/* PROGRAMMES */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }} data-testid="pets-programmes">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                        <h2 className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 500, margin: 0, letterSpacing: "0.01em" }}>Programmes</h2>
                        <p style={{ color: "#888", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0 }}>Single · Protocol · Household</p>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                        <Programme
                            tier="Single Session"
                            subtitle="One Pet"
                            headline="One-off in-home recovery — ideal for fireworks night, post-trauma reset or a trial."
                            items={["In-home, 60 minutes", "Owner intake + debrief", "48-hr observation guide", "Audio recommendations"]}
                            testid="pets-prog-single"
                        />
                        <Programme
                            tier="4-Session Protocol"
                            subtitle="Over 4 Weeks"
                            headline="The standard for chronic anxiety, rescue trauma and behavioural retraining."
                            featured
                            items={["Weekly cadence", "Tracked outcomes", "Most owners see change by session three", "Priority scheduling"]}
                            testid="pets-prog-protocol"
                        />
                        <Programme
                            tier="Multi-Pet"
                            subtitle="2 – 5 Animals"
                            headline="One session covering the whole household — powerful after conflict, arrival or loss."
                            items={["Group regulation", "Whole-home reset", "Owner included", "Aftercare plan"]}
                            testid="pets-prog-multi"
                        />
                    </div>
                </section>

                {/* CTA + CONTACT */}
                <section style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 18, marginBottom: 8 }}>
                    <div>
                        <h2 className="font-serif" style={{ color: "#fff", fontSize: 20, fontWeight: 400, margin: "0 0 6px", letterSpacing: "-0.01em" }}>
                            Settle the Pet. <span style={{ color: GOLD }}>Settle the Home.</span>
                        </h2>
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.55, margin: 0, maxWidth: 380, fontWeight: 300 }}>
                            Reserve via WhatsApp. Same-day confirmation. We come to you — UAE-wide.
                        </p>
                    </div>
                    <div style={{ textAlign: "right", fontFamily: "Inter, sans-serif" }}>
                        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500 }}>Reserve via WhatsApp</p>
                        <p className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 500, margin: "0 0 6px", letterSpacing: "0.02em" }}>{PHONE_DISPLAY}</p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>{EMAIL}</p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>theresetsanctuary.org</p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 10px" }}>Instagram {INSTAGRAM}</p>
                        <a href={petsWa} target="_blank" rel="noopener noreferrer" data-testid="pets-cta" className="brochure-screen-only" style={{ display: "inline-block", color: "#0a0a0a", backgroundColor: GOLD, padding: "10px 18px", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600, textDecoration: "none" }}>Book In-Home</a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p style={{ color: "#666", fontSize: 7, lineHeight: 1.5, textAlign: "center", margin: "0 0 10px", position: "relative", zIndex: 1, paddingTop: 8, borderTop: `1px solid ${GOLD_LINE_SOFT}` }}>
                    The Reset Sanctuary offers wellness and recovery sessions through precision sound and frequency work. We do not diagnose, treat or cure veterinary conditions. Our services are complementary wellness practices, not veterinary care. Please consult a licensed veterinarian for medical concerns. © 2026 Tatva K.
                </p>

                <PageFooter pageNum={2} tagline="Berklee · ISTA · IPHM · Insight Timer" />
            </PageFrame>
        </main>
    );
};

export default BrochurePets;
